
import { getAuthToken } from '@/utils/auth/tokenManager';
import api from '@/api/Api';
import type { WidgetField } from '@/types/widget';

// Define types for Widget API response
export interface WidgetTheme {
  id: string;
  name: string;
  colors: {
    primaryForegroundColor?: string;
    inputBackgroundColor?: string;
    borderColor?: string;
    textColor: string;
    primaryColor: string;
    backgroundColor: string;
    userMessageBackgroundColor: string;
    agentMessageBackgroundColor: string;
  };
  labels: {
    welcomeTitle: string;
    welcomeMessage: string;
    welcomeSubtitle: string;
    askQuestionButtonText: string;
  };
  layout: {
    width?: number;
    height?: number;
    launcherSize?: number;
    cornerRadius?: number;
    showShadow?: boolean;
    showCloseIcon?: boolean;
    offsetX: number;
    offsetY: number;
    isCompact: boolean;
    placement: string;
  };
  widgetId: string;
  brandAssets: {
    headerLogo: string;
    launcherIcon: string;
  };
  widgetSettings: {
    allowedDomains: string[];
  };
  interfaceSettings: {
    showBrandingBar: boolean;
    showOfficeHours: boolean;
    showAgentPresence: boolean;
    showAgentChatStatus: boolean;
    showTicketStatusBar: boolean;
    enableMessageReaction: boolean;
    allowVisitorsToEndChat: boolean;
    enableConversationRating: boolean;
    enableDeliveryReadReceipts: boolean;
  };
}

export interface Widget {
  id: string;
  name: string;
  clientId: string;
  workspaceId: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: null | string;
  widgettheme: WidgetTheme[];
  widgetfield: WidgetField[];
  widgetapikeyrelation: {
    apiKey: string;
  }[];
}

export interface WidgetResponse {
  status: string;
  message: string;
  data: Widget;
}

export interface UpdateWidgetThemeRequest {
  name?: string;
  colors?: Partial<WidgetTheme['colors']>;
  labels?: Partial<WidgetTheme['labels']>;
  layout?: Partial<WidgetTheme['layout']>;
  brandAssets?: Partial<WidgetTheme['brandAssets']>;
  widgetSettings?: Partial<WidgetTheme['widgetSettings']>;
  interfaceSettings?: Partial<WidgetTheme['interfaceSettings']>;
}

// Widget service to handle API calls
export const widgetService = {
  async getWidgetConfiguration(): Promise<WidgetResponse> {
    try {
      const workspaceId = localStorage.getItem('workspaceId');
      const token = getAuthToken();

      if (!workspaceId) {
        throw new Error('Workspace ID not found');
      }

      const response = await api.get('/widgets', {
        params: { workspace_id: workspaceId },
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined
        }
      });

      return response.data;
    } catch (error) {
      console.error('Error fetching widget configuration:', error);
      throw error;
    }
  },

  async updateWidgetTheme(widgetId: string, themeId: string, updates: UpdateWidgetThemeRequest): Promise<WidgetResponse> {
    try {
      const workspaceId = localStorage.getItem('workspaceId');
      const token = getAuthToken();

      if (!workspaceId) {
        throw new Error('Workspace ID not found');
      }

      const response = await api.patch(`/widgets`, {
        widgetTheme: {
          ...updates,
          widgetId
        }
      }, {
        params: { workspace_id: workspaceId },
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          'Content-Type': 'application/json'
        }
      });

      console.log('Widget theme update response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error updating widget theme:', error);
      throw error;
    }
  },

  async updateWidgetSettings(widgetId: string, updates: Partial<Omit<Widget, 'widgettheme' | 'id'>>): Promise<WidgetResponse> {
    try {
      const workspaceId = localStorage.getItem('workspaceId');
      const token = getAuthToken();

      if (!workspaceId) {
        throw new Error('Workspace ID not found');
      }

      const response = await api.patch(`/widgets`, updates, {
        params: { workspace_id: workspaceId },
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined
        }
      });

      console.log('Widget settings update response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error updating widget settings:', error);
      throw error;
    }
  },

  async uploadWidgetAsset(file: File, token: string = null): Promise<{ fileUrl: string }> {
    return new Promise((resolve, reject) => {
      const workspaceId = localStorage.getItem('workspaceId');

      if (!workspaceId) {
        reject(new Error('Workspace ID not found'));
        return;
      }

      const formData = new FormData();
      formData.append('file', file);

      const xhr = new XMLHttpRequest();
      xhr.withCredentials = false;
      const url = `${api.defaults.baseURL}/widgets/uploadWidgetAssets?workspace_id=${workspaceId}`;

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress = (event.loaded / event.total) * 100;
          // Dispatch a custom event that components can listen to
          window.dispatchEvent(new CustomEvent('widgetAssetUploadProgress', {
            detail: { progress }
          }));
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          const response = JSON.parse(xhr.responseText);
          resolve(response?.data?.fileUrl);
        } else {
          reject(new Error('Upload failed'));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed'));
      });

      xhr.open('POST', url);
      xhr.setRequestHeader('Authorization', token ? `Bearer ${token}` : '');
      xhr.send(formData);
    });
  },

  async updateWidgetFields(widgetId: string, fields: WidgetField[]): Promise<any> {
    try {
      const workspaceId = localStorage.getItem('workspaceId');
      const token = getAuthToken();

      if (!workspaceId) {
        throw new Error('Workspace ID not found');
      }

      // Transform fields to match required format
      const widgetFields = fields.map((field, index) => ({
        entityname: field.entityType || field.table,
        columnname: field.columnname,
        label: field.label,
        type: field.type,
        placeholder: field.placeholder || null,
        required: field.required || false,
        position: index + 1
      }));

      const response = await api.patch(`/widgets`, {
        widgetField: widgetFields
      }, {
        params: { workspace_id: workspaceId },
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined
        }
      });

      return response.data;
    } catch (error) {
      console.error('Error updating widget fields:', error);
      throw error;
    }
  }
};
